/**
 * Constantes partagées du frontend (enums, pastilles de couleur, seuils).
 * Les clés sont alignées sur le backend (app/constants.py). Aucune valeur magique ailleurs.
 */

// Pastilles de couleur du vin (oklch de la maquette) — clé -> {couleur, libellé}.
export const WINE_COLOR_DOTS = {
  rouge: { color: 'oklch(0.45 0.13 20)', label: 'Rouge' },
  blanc: { color: 'oklch(0.92 0.07 100)', label: 'Blanc' },
  rose: { color: 'oklch(0.8 0.09 10)', label: 'Rosé' },
  orange: { color: 'oklch(0.74 0.12 60)', label: 'Orange' },
  petillant: { color: 'oklch(0.88 0.1 95)', label: 'Pétillant' },
  doux: { color: 'oklch(0.72 0.13 75)', label: 'Doux' },
  gris: { color: 'oklch(0.85 0.03 60)', label: 'Gris' },
  vert: { color: 'oklch(0.88 0.09 120)', label: 'Vert' },
};

// Ordre d'affichage des couleurs dans les filtres.
export const COLOR_FILTER_ORDER = ['rouge', 'blanc', 'rose', 'orange', 'gris', 'vert'];

// Contextes de dégustation (carnet « Vins dégustés »).
export const TASTING_CONTEXTS = [
  { value: 'salon', label: 'Salon' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'amis', label: 'Chez des amis' },
  { value: 'fournisseur', label: 'Fournisseur' },
  { value: 'autre', label: 'Autre' },
];
// Filtres segmentés du carnet (avec « Tous »).
export const TASTING_CONTEXT_FILTERS = [
  { value: '', label: 'Tous' },
  { value: 'salon', label: 'Salons' },
  { value: 'restaurant', label: 'Restaurants' },
  { value: 'amis', label: 'Chez des amis' },
  { value: 'fournisseur', label: 'Fournisseurs' },
];
// Pictogrammes des sources d'une recommandation « À découvrir ».
export const RECO_SOURCE_LABELS = {
  google_maps: 'Google Maps', site_web: 'Site web', photo: 'Photo',
  degustation: 'Dégustation', manuel: 'Manuel',
};


// Libellés des types de vin.
export const WINE_TYPE_LABELS = {
  tranquille: 'Tranquille', perle: 'Perlé', petillant: 'Pétillant', mousseux: 'Mousseux',
  doux: 'Doux', liquoreux: 'Liquoreux', fortifie: 'Fortifié',
};

// Spécificités (clé -> libellé).
export const SPECIFICITY_LABELS = {
  bio: 'Bio · AB', biodynamie: 'Biodynamie', nature: 'Vin nature',
  sans_sulfites: 'Sans sulfites ajoutés', vegan: 'Vegan', hve: 'HVE',
  vendange_manuelle: 'Vendange manuelle',
};

// Statuts de cuvée -> libellé + style de tag.
export const STATUS_LABELS = {
  rupture: 'Rupture', stock_faible: 'Stock faible', a_boire: 'À boire', recent: 'Récent', deguste: 'Dégusté',
};

// Types d'alertes -> libellé.
export const ALERT_LABELS = {
  rupture: 'Rupture', stock_faible: 'Stock faible', a_boire: 'À boire',
  facture: 'À vérifier', inventaire: 'Écart',
};

// Axes du radar de dégustation (ordre + libellés).
export const TASTING_AXES = [
  { key: 'sucre', label: 'Sec → moelleux' },
  { key: 'acidite', label: 'Acidité' },
  { key: 'tanin', label: 'Tanin' },
  { key: 'corps', label: 'Corps' },
  { key: 'fruit', label: 'Fruit' },
  { key: 'epices', label: 'Épices' },
];

// Sources d'un champ pré-rempli par l'IA.
export const SOURCE_LABELS = {
  etiquette: 'Étiquette', contre_etiquette: 'Contre-étiquette', facture: 'Facture',
  web: 'Web', domaine: 'Site du domaine', cave: 'Votre cave',
};

// Libellés des langues de la carte des vins (endonymes).
export const LANGUAGE_LABELS = {
  fr: 'Français', en: 'English', nl: 'Nederlands', de: 'Deutsch', es: 'Español',
};

export const UNDO_DELAY_MS = 10000;
export const SEARCH_DEBOUNCE_MS = 250;
export const PHOTO_MAX_PX = 1600;

// Caméra (getUserMedia) — jamais de contrainte `exact` (échoue sur certains iPad).
export const CAMERA_CONSTRAINTS = { video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 } }, audio: false };
// Durée du flash blanc à la capture (ms).
export const CAPTURE_FLASH_MS = 180;
// Délai avant enregistrement automatique des réglages (ms).
export const AUTOSAVE_DEBOUNCE_MS = 600;

// Palette restreinte (8 teintes) pour la couleur des tags (miroir backend TAG_COLORS).
export const TAG_COLORS = ['#b68235', '#7b2233', '#4a7a3a', '#3f7d8a', '#6d3b6e', '#d0863a', '#3f8a6e', '#a0306a'];

// Palette des régions viticoles (miroir de components/map/wineRegions.js).
export const WINE_REGION_COLORS = {
  Bordeaux: '#7b2233', Bourgogne: '#9c4a2a', Beaujolais: '#7a4fa0', 'Vallée du Rhône': '#8a2e2e',
  'Languedoc-Roussillon': '#a0306a', 'Sud-Ouest': '#6d3b6e', Provence: '#d0863a',
  'Vallée de la Loire': '#3f8aa0', Champagne: '#b98a2e', Alsace: '#5a8a3a', 'Jura · Savoie': '#3f8a6e', Corse: '#8a5a2a',
};

// Modes d'affichage de la cave (Stock).
export const STOCK_GROUPINGS = [
  { value: 'cuvee', label: 'Par cuvée' },
  { value: 'vignoble', label: 'Par vignoble' },
  { value: 'emplacement', label: 'Par emplacement' },
];

// Types de document détectables par le scanner.
export const DOC_TYPE_LABELS = {
  etiquette: 'Étiquette',
  contre_etiquette: 'Contre-étiquette',
  facture: 'Facture',
  bon_de_livraison: 'Bon de livraison',
  ticket: 'Ticket',
  qr_casier: 'QR casier',
};

// Raccourcis de quantité (mode entrée).
export const QUANTITY_SHORTCUTS = [0, 6, 12, 24];

// Formats de bouteille (pour souhaits de commande et ajout à la cave).
export const BOTTLE_FORMAT_OPTIONS = [
  { value: '750', label: 'Bouteille (75 cl)' },
  { value: '375', label: 'Demi (37,5 cl)' },
  { value: '500', label: '50 cl' },
  { value: 'magnum', label: 'Magnum (1,5 L)' },
  { value: 'jeroboam', label: 'Jéroboam (3 L)' },
];

// Motifs de sortie (ordre + libellés d'affichage).
export const EXIT_MOTIFS = [
  { key: 'table', label: 'À table' },
  { key: 'emporter', label: 'À emporter' },
  { key: 'verre', label: 'Au verre' },
  { key: 'degustation', label: 'Dégustation' },
  { key: 'offert_perso', label: 'Offert · perso' },
  { key: 'casse', label: 'Casse' },
];
